/**
 * Per-stock research view.
 *
 * The `not_claimed` list from the engine is rendered as part of the panel, not
 * as a footnote, because it is the part a tool like this normally leaves out.
 */
import { formatNum, formatPct } from "@tt/shared";

import { useAsync } from "../hooks/useApi";
import { api } from "../lib/api";
import { VerdictBadge } from "../components/Verdict";

export function ResearchPanel({ symbol, timeframe }: { symbol: string; timeframe: string }) {
  const { data, loading, error } = useAsync(
    (s) => api.research(symbol, timeframe, s),
    [symbol, timeframe],
  );

  if (loading) {
    return (
      <div className="panel">
        <div className="hint">
          <span className="spinner" /> Computing indicators, evaluating signals and
          summarising coverage for {symbol}…
        </div>
      </div>
    );
  }
  if (error) return <div className="panel"><div className="err">{error.message}</div></div>;
  if (!data) return <div className="panel"><div className="empty">No data.</div></div>;

  const t = data.technicals;
  const cells: [string, string, string?][] = [
    ["price", formatNum(t.price)],
    ["RSI(14)", formatNum(t.rsi14, 1), t.rsi14 === null ? undefined : t.rsi14 > 70 ? "down" : t.rsi14 < 30 ? "up" : ""],
    ["ADX(14)", formatNum(t.adx14, 1)],
    ["ATR %", formatNum(t.atr_pct, 2)],
    ["realised vol", `${formatNum(t.realised_vol_pct, 0)}%`],
    ["rel. volume", formatNum(t.relative_volume, 2)],
    ["20-day MA", formatNum(t.sma20)],
    ["50-day MA", formatNum(t.sma50)],
    ["200-day MA", formatNum(t.sma200)],
    ["band position", `${formatNum(t.bb_position_pct, 0)}%`],
    ["1-month", `${formatNum(t.return_1m_pct, 1)}%`, (t.return_1m_pct ?? 0) >= 0 ? "up" : "down"],
    ["3-month", `${formatNum(t.return_3m_pct, 1)}%`, (t.return_3m_pct ?? 0) >= 0 ? "up" : "down"],
    ["12-month", `${formatNum(t.return_12m_pct, 1)}%`, (t.return_12m_pct ?? 0) >= 0 ? "up" : "down"],
    ["52-week high", formatNum(t.high_52w)],
    ["52-week low", formatNum(t.low_52w)],
    ["off the high", `${formatNum(t.pct_off_52w_high, 1)}%`],
  ];

  const news = data.news && "articles" in data.news ? data.news : null;

  return (
    <div className="panel">
      <div className="row" style={{ marginBottom: 8 }}>
        <h3 style={{ margin: 0 }}>{data.symbol} · state of play</h3>
        <span className="spacer" />
        <span className="badge muted">{data.bars} bars</span>
        <span className="badge muted">{data.provider}</span>
        {data.delayed && (
          <span className="badge warn" title="Prices are not real-time on this feed">
            delayed
          </span>
        )}
      </div>

      <div className="kv">
        {cells.map(([k, v, cls]) => (
          <div className="kv-cell" key={k}>
            <div className="k">{k}</div>
            <div className={`v ${cls ?? ""}`}>{v}</div>
          </div>
        ))}
      </div>

      <h3>What the numbers say</h3>
      <ul className="tight muted">
        {data.interpretation.observations.map((o, i) => <li key={i}>{o}</li>)}
      </ul>

      <h3>Signals firing · {data.interpretation.edge_summary}</h3>
      {data.signals_fired.length === 0 ? (
        <div className="empty">No built-in signals fired in the last 5 bars.</div>
      ) : (
        <div className="table-scroll">
          <table className="grid">
            <thead>
              <tr><th>Signal</th><th>Side</th><th>When</th><th>10-bar track record</th></tr>
            </thead>
            <tbody>
              {data.signals_fired.map((s, i) => {
                const rec = s.track_record["10"] ?? s.track_record["5"];
                return (
                  <tr key={i}>
                    <td>
                      <strong>{s.label}</strong>
                      <div className="faint small">{s.description}</div>
                    </td>
                    <td className={s.direction > 0 ? "up" : "down"}>
                      {s.direction > 0 ? "long" : "short"}
                    </td>
                    <td className="small nowrap">
                      {s.bars_ago === 0 ? "latest bar" : `${s.bars_ago} bars ago`}
                    </td>
                    <td>
                      {rec ? (
                        <>
                          <VerdictBadge detail={rec} />
                          <div className="faint small mono">
                            {formatPct(rec.hit_rate)} vs {formatPct(rec.baseline)} baseline, n={rec.samples}
                          </div>
                          {rec.notes.length > 0 && (
                            <div className="faint small">{rec.notes[0]}</div>
                          )}
                        </>
                      ) : (
                        <span className="faint small">not calibrated</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {news && news.articles > 0 && (
        <>
          <h3>Recent coverage · {news.articles} scored articles</h3>
          <div className="kv" style={{ marginBottom: 8 }}>
            <div className="kv-cell">
              <div className="k">weighted tone</div>
              <div
                className="v"
                style={{
                  color: (news.sentiment ?? 0) > 0.15 ? "var(--up)"
                    : (news.sentiment ?? 0) < -0.15 ? "var(--down)" : undefined,
                }}
              >
                {news.sentiment === null ? "—" : news.sentiment.toFixed(2)}
              </div>
            </div>
            <div className="kv-cell">
              <div className="k">peak materiality</div>
              <div className="v">{news.max_materiality?.toFixed(2) ?? "—"}</div>
            </div>
          </div>
          {news.top_stories.slice(0, 5).map((s, i) => (
            <div key={i} style={{ borderBottom: "1px solid var(--border)", padding: "6px 0" }}>
              <div className="row" style={{ alignItems: "baseline" }}>
                <span className={`badge ${s.sentiment > 0.15 ? "good" : s.sentiment < -0.15 ? "bad" : "muted"}`}>
                  {s.sentiment > 0 ? "+" : ""}{s.sentiment.toFixed(2)}
                </span>
                <span className="badge">M {s.materiality.toFixed(2)}</span>
                <span className="grow">
                  {s.url ? (
                    <a href={s.url} target="_blank" rel="noreferrer noopener"
                       style={{ color: "var(--text)", textDecoration: "none" }}>
                      {s.headline}
                    </a>
                  ) : s.headline}
                </span>
                <span className="faint small nowrap">{s.source}</span>
              </div>
              {s.rationale && <div className="faint small">{s.rationale}</div>}
            </div>
          ))}
        </>
      )}
      {news && news.articles === 0 && (
        <>
          <h3>Recent coverage</h3>
          <div className="empty">
            No scored articles for {symbol}. Ingest and score news from the News tab.
          </div>
        </>
      )}

      <h3>What this view does not claim</h3>
      <div className="notebox">
        <ul className="tight">
          {data.interpretation.not_claimed.map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      </div>
    </div>
  );
}
